import { Box, Stack } from "@mui/material";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  useFindSessionSessionsSessionIdGetQuery,
  useGenerateReportsReportsGetQuery,
} from "api";
import { LoadingPage, NoDataPage } from "components/pages";

import { SessionSelector } from "./components/SessionSelector";
import { SleepDetailsSection } from "./sections/SleepDetailsSection";
import {
  DeviationMetric,
  metricKeys,
} from "./sections/SleepOverviewSection/components/DeviationText";
import { SleepOverviewSection } from "./sections/SleepOverviewSection/SleepOverviewSection";

import { Header } from "../components/Header";

type SessionsProps = {
  subjectId: string;
};

export function Sessions({ subjectId }: SessionsProps) {
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();

  const sessionId = searchParams.get("sessionId");

  const { data: sessionData, isLoading: isLoadingSession } =
    useFindSessionSessionsSessionIdGetQuery(
      {
        sessionId: sessionId ?? "",
        includeEpochData: true,
      },
      {
        skip: !sessionId,
      },
    );

  const session = sessionData?.data ?? null;

  const { data: nightReportData, isLoading: isLoadingNightReport } =
    useGenerateReportsReportsGetQuery(
      {
        subjects: [session?.subject_id ?? ""],
        reportDate: format(session?.session_end ?? new Date(), "yyyy-MM-dd"),
      },
      {
        skip: !session,
      },
    );

  const nightReport = nightReportData?.data?.[0] ?? null;

  if (isLoadingSession || isLoadingNightReport || !sessionId) {
    return (
      <Box>
        <Header title={t("Sessions")} />
        <SessionSelector
          subjectId={subjectId}
          nightSessionId={nightReport?.session_id}
        />
        <LoadingPage />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box>
        <Header title={t("Sessions")} />
        <SessionSelector
          subjectId={subjectId}
          nightSessionId={nightReport?.session_id}
        />
        <NoDataPage
          title={"Session not found"}
          message={"Try selecting another"}
        />
      </Box>
    );
  }

  const activeReport =
    nightReport?.session_id == sessionId ? nightReport : null;

  const deviations = metricKeys
    .map((d) => ({ id: d, metric: activeReport?.[d] ?? null }))
    .filter((d) => d.metric?.deviation_alert)
    .map((d) => ({ id: d.id, key: d.id, ...d.metric })) as DeviationMetric[];

  return (
    <Box
      sx={{
        "@media print": {
          width: "1500px",
        },
      }}
    >
      <Header title={t("Sessions")} />
      <SessionSelector
        subjectId={subjectId}
        nightSessionId={nightReport?.session_id}
      />
      <Stack gap={2}>
        <SleepOverviewSection
          sleepScoreMetric={activeReport?.sleep_score ?? null}
          totalSleepTimeMetric={activeReport?.time_asleep ?? null}
          timesOutOfBedMetric={activeReport?.out_of_bed ?? null}
          movementMetric={activeReport?.movement ?? null}
          deviations={activeReport ? deviations : null}
          session={{
            timeAsleep: session.time_asleep,
            sleepScoreStandard: session.sleep_score_standard,
            numberOfTimesNoPresence: session.number_of_times_no_presence,
            epochsWithMovementPct: session.epochs_with_movement_pct,
            airQualityFilteredMean: session.indoor_air_quality_filtered_mean,
            airHumidityFilteredMean: session.air_humidity_filtered_mean,
            temperatureFilteredMean: session.temperature_filtered_mean,
          }}
        />
        <SleepDetailsSection session={session} />
      </Stack>
    </Box>
  );
}
